import React from "react";
import cx from "classnames";

import { MediaAsset, TranscodeStatus } from "mediaModules/media/models";
import { AudioPlayerContainer } from "mediaModules/media/components/AudioPlayerContainer";
import { TrackViewer } from "mediaModules/media/pages/EditAudioPage/components/TrackViewer";

import { IExternalMedia } from "shared-types";
import { externalMediaMapToAudioModel } from "shared-services";
import { heapTrackingForAudioClips } from "app/helpers/heapTracking";
import "./AudioClips.scss";

export interface IAudioClipsProps {
  audioClips: MediaAsset[];
  openPopup: (popupContent, popupProps) => void;
  closePopup: () => void;
  mediaObjectPromiseMap: Map<string, Promise<MediaAsset>>;
  showreelsLength: number;
  updateProcessedAudioClipsCount: (count: number) => void;
  externalAudio?: IExternalMedia[];
}

const TITLE_MAX_LINES = 3;

const mapToAudioModel = (mediaAsset: MediaAsset) => {
  const { id, durationInMs, sources, title } = mediaAsset;

  return {
    id,
    src: sources[0],
    duration: durationInMs,
    fileName: title,
  };
};

export const AudioClips: React.FC<React.PropsWithChildren<IAudioClipsProps>> = (
  props
) => {
  const [audioClips, setAudioClips] = React.useState<MediaAsset[]>(
    props.audioClips
  );

  const updateAudioClipsByMediaObjects = async () => {
    const updatedAudioClipsPromises = audioClips.map(
      (audioClip) => props.mediaObjectPromiseMap.get(audioClip.mediaInfoId)!
    );
    const updatedAudioClips = await Promise.all(updatedAudioClipsPromises);
    const withCompleteStatus = updatedAudioClips.filter(
      ({ transcodeStatus }) => transcodeStatus === TranscodeStatus.Complete
    );
    setAudioClips(withCompleteStatus);
    props.updateProcessedAudioClipsCount(withCompleteStatus.length);
  };

  React.useEffect(() => {
    if (!(props.externalAudio && props.externalAudio.length > 0)) {
      updateAudioClipsByMediaObjects();
    }
  }, []);

  const openAudioPopup = (audioClip: MediaAsset) => {
    heapTrackingForAudioClips();

    const currentTrack = mapToAudioModel(audioClip);

    const audioPlayer = (
      <AudioPlayerContainer
        playerConfig={{
          currentTrack,
          tracks: [],
          isAutoPlay: true,
          selectTrack: () => undefined,
        }}
        closeAction={props.closePopup}
      />
    );

    props.openPopup(audioPlayer, { hideCloseButton: true, fullHeight: true });
  };

  const openExternalAudioPopup = (audioClip: IExternalMedia) => {
    const currentTrack = externalMediaMapToAudioModel(audioClip);

    const audioPlayer = (
      <AudioPlayerContainer
        playerConfig={{
          currentTrack,
          tracks: [],
          isAutoPlay: true,
          selectTrack: () => undefined,
        }}
        closeAction={props.closePopup}
      />
    );

    props.openPopup(audioPlayer, { hideCloseButton: true, fullHeight: true });
  };

  const colSize = props.showreelsLength > 0 ? 12 : 6;
  const containerClassName = cx(
    `c-media-section-audio-clips__audio-clip g-col-md-${colSize} g-col-lg-${colSize}`,
    { "no-video": !props.showreelsLength }
  );

  const renderAudioClip = (audioClip: MediaAsset) => {
    return (
      <div key={audioClip.id} className={containerClassName}>
        <TrackViewer
          title={audioClip.title}
          audioModel={audioClip}
          playback={openAudioPopup}
          titleMaxLines={TITLE_MAX_LINES}
        />
      </div>
    );
  };

  const renderExternalAudioClip = (audioClip: IExternalMedia) => {
    return (
      <div key={audioClip.id} className={containerClassName}>
        <TrackViewer
          title={audioClip.title}
          audioModel={audioClip}
          playback={openExternalAudioPopup}
          titleMaxLines={TITLE_MAX_LINES}
        />
      </div>
    );
  };

  return (
    <div className="g-inner-grid g-col-lg-12 c-media-section-audio-clips">
      {audioClips.map(renderAudioClip)}
      {props.externalAudio?.map(renderExternalAudioClip)}
    </div>
  );
};
